import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../../shared/api/axios';
import { Button } from '../../components/ui/button';
import SmartVideoPlayer from '../../pages/Presentation/SmartVideoPlayer';
import { useMediaPreview } from '../../../shared/contexts/MediaPreviewContext';
import { getWebFilterStyle, type FilterPresetId,} from '../../../shared/components/filterHelpers.web';
import { useTranslation } from 'react-i18next';

type MusicTrack = {
  _id: string;
  title: string;
  artist: string;
  s3Url?: string;
  url?: string;
  audioUrl?: string;
  duration?: number;
  file?: File; // ✅ ADD
};

type LocState = {
  editTargetId?: string;
  remoteUrl?: string;
  videoUrl?: string;
  sidebarData?: any;
  track: MusicTrack;
  videoFile?: File | null;   // ✅ NEW
};

const TRACK_H = 72;
const HANDLE_W = 3;
const WINDOW_RATIO = 0.72;       // same visual proportion as mobile
const SNAP_MS = 50;
const THUMB_W = 18;
const BAR_W = 2;
const BAR_GAP = 1;
const BAR_STEP = BAR_W + BAR_GAP * 2;

const PSEUDO_DUR_MS = 180_000;   // when track duration unknown
const MIN_MS = 1_000;            // 1s floor (you can raise to 3_000)
const MAX_MS = 180_000;          // 3m cap

function getApiBaseUrl() {
  const env = process.env.REACT_APP_API_BASE_URL;
  return env ? env.replace(/\/api\/?$/, '') : 'http://localhost:3001';
}

function toAbsoluteHttp(u?: string | null): string | undefined {
  if (!u) return undefined;
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith('/')) return `${getApiBaseUrl()}${u}`;
  return undefined;
}

function getTrackUrl(t?: MusicTrack | null) {
  return t?.audioUrl || t?.url || t?.s3Url || '';
}

const clampWindow = (ms: number) =>
  Math.max(MIN_MS, Math.min(MAX_MS, Math.floor(ms || 0)));

const fmt = (msOrSec?: number | null, unit: 'ms' | 's' = 's') => {
  if (msOrSec == null || !isFinite(msOrSec)) return '0:00';
  const totalSec = unit === 'ms' ? Math.max(0, Math.round(msOrSec / 1000)) : Math.max(0, Math.round(msOrSec));
  const m = Math.floor(totalSec / 60);
  const s = String(totalSec % 60).padStart(2, '0');
  return `${m}:${s}`;
};

// fnv-ish bars like RN
function makeBars(seed: string, n: number) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < seed.length; i++) { h ^= seed.charCodeAt(i); h = Math.imul(h, 16777619); }
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    h ^= h << 13; h ^= h >>> 17; h ^= h << 5;
    const v = ((h >>> 0) % 1000) / 1000;
    out.push(Math.max(0.12, Math.pow(v, 0.7)));
  }
  return out;
}

const AdChooseTrack: React.FC = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { t } = useTranslation();

  const { editTargetId, remoteUrl, videoUrl: legacyVideoUrl, sidebarData, track, videoFile } =
  (state || {}) as LocState;

  const { mediaItems, addOrReplaceMediaItem, updateMediaMusic } = useMediaPreview();
  
  //Filtered Media
  const filterId: FilterPresetId =
  (editTargetId ? (mediaItems.find(m => m.id === editTargetId)?.filterId as FilterPresetId) : undefined) ?? 'none';
const { cssFilter, overlay } = getWebFilterStyle(filterId);

  // resolve video source (prefer File from context)
  const createdObjectUrlRef = React.useRef<string | null>(null);
  const srcFileRef = React.useRef<File | null>(null);
  const [videoUrl, setVideoUrl] = React.useState<string | null>(null);
// put this near your other state
const [pendingStart, setPendingStart] = React.useState(0);
const commitTimerRef = React.useRef<number | null>(null);

const isHttp = (u?: string | null) => !!u && /^https?:\/\//i.test(u);
const toHttp = (u?: string | null) =>
  !u ? undefined : isHttp(u) ? u : u?.startsWith('/') ? `${getApiBaseUrl()}${u}` : undefined;

  

  const urlRef = React.useRef<string | null>(null);

React.useEffect(() => {
  // Prefer the File explicitly passed in navigation state
  if (videoFile instanceof File) {
    srcFileRef.current = videoFile;
    if (!urlRef.current) urlRef.current = URL.createObjectURL(videoFile);
    setVideoUrl(urlRef.current);
    return;
  }

  // Else try context
  const ctxFile = editTargetId
    ? mediaItems.find(m => m.id === editTargetId)?.file
    : undefined;

  if (ctxFile instanceof File) {
    srcFileRef.current = ctxFile;
    if (!urlRef.current) urlRef.current = URL.createObjectURL(ctxFile);
    setVideoUrl(urlRef.current);
    return;
  }

  // Else absolute HTTP(S)
  srcFileRef.current = null;
  setVideoUrl(toHttp(remoteUrl) || toHttp(legacyVideoUrl) || null);
}, [videoFile, editTargetId, mediaItems, remoteUrl, legacyVideoUrl]);

// ⛔️ Remove any cleanup that revokes urlRef.current

  
  // probe durations
  const hiddenVideoRef = React.useRef<HTMLVideoElement | null>(null);
  const [videoDuration, setVideoDuration] = React.useState<number | null>(null); // seconds
  const hiddenAudioRef = React.useRef<HTMLAudioElement | null>(null);
  const [trackDuration, setTrackDuration] = React.useState<number | null>(track?.duration ?? null);

  React.useEffect(() => {
    const v = hiddenVideoRef.current;
    if (!v || !videoUrl) return;
    const onMeta = () => { if (Number.isFinite(v.duration)) setVideoDuration(v.duration); };
    v.addEventListener('loadedmetadata', onMeta);
    v.load();
    return () => v.removeEventListener('loadedmetadata', onMeta);
  }, [videoUrl]);

  React.useEffect(() => {
    if (trackDuration || !track) return;
    const a = hiddenAudioRef.current;
    const src = localAudioUrl || toAbsoluteHttp(getTrackUrl(track));
    if (!a || !src) return;
    const onMeta = () => { if (Number.isFinite(a.duration)) setTrackDuration(a.duration); };
    a.addEventListener('loadedmetadata', onMeta);
    a.src = src;
    a.load();
    return () => a.removeEventListener('loadedmetadata', onMeta);
  }, [track, trackDuration]);

  // window (= video length) and start position
  const windowMs = React.useMemo(() => clampWindow((videoDuration ?? 0) * 1000), [videoDuration]);
  const totalMs = React.useMemo(() => (trackDuration ? Math.floor(trackDuration * 1000) : PSEUDO_DUR_MS), [trackDuration]);
  const maxStart = Math.max(0, totalMs - windowMs);

  const [startMs, _setStartMs] = React.useState(0);
  const startMsRef = React.useRef(0);
  const setStartMs = (v: number) => { startMsRef.current = v; _setStartMs(v); };
  const [isDragging, setDragging] = React.useState(false);
// live value for visuals
const uiStartMs = isDragging ? pendingStart : startMs;
const liveStartMs = uiStartMs;
const liveEndMs = Math.min(uiStartMs + windowMs, totalMs);

  React.useEffect(() => { setStartMs(Math.min(startMsRef.current, maxStart)); }, [maxStart]);

  // layout & visual math (center-anchored selection like RN)
  const railRef = React.useRef<HTMLDivElement | null>(null);
  const [railW, setRailW] = React.useState(0);
  React.useLayoutEffect(() => {
    const r = railRef.current;
    if (!r) return;
    const on = () => setRailW(r.clientWidth);
    on();
    const obs = new ResizeObserver(on);
    obs.observe(r);
    return () => obs.disconnect();
  }, []);

  const selW = Math.max(24, Math.round(railW * WINDOW_RATIO));
  const ppm = selW > 0 ? selW / windowMs : 0; // px per ms inside the window

  const leftAnchor = 0;
  const centerAnchor = Math.max(0, Math.round((railW - selW) / 2));
  const rightAnchor = Math.max(0, railW - selW);
  const windowHalf = windowMs / 2;

  // selection left position
const selLeft = React.useMemo(() => {
  if (railW <= 0) return 0;
  const s = uiStartMs;                         // 👈 use UI live value
  if (s <= windowHalf) {
    const t = windowHalf > 0 ? s / windowHalf : 0;
    return Math.round(leftAnchor + (centerAnchor - leftAnchor) * t);
  }
  const rightPhaseStart = Math.max(0, maxStart - windowHalf);
  if (s >= rightPhaseStart) {
    const denom = windowHalf || 1;
    const t = Math.min(1, Math.max(0, (s - rightPhaseStart) / denom));
    return Math.round(centerAnchor + (rightAnchor - centerAnchor) * t);
  }
  return centerAnchor;
}, [railW, uiStartMs, windowHalf, leftAnchor, centerAnchor, rightAnchor, maxStart]);

// wave offset
const waveTranslateX = selLeft - uiStartMs * ppm;   // 👈 use UI live value
const snappedWaveX = Math.round(waveTranslateX);

  const waveWidthPx = Math.max(railW, Math.ceil(totalMs * ppm));
  const BAR_COUNT = Math.max(1, Math.ceil(waveWidthPx / BAR_STEP) + 2);
  const bars = React.useMemo(
    () => makeBars(track?._id || track?.title || 'seed', BAR_COUNT),
    [track?._id, track?.title, BAR_COUNT]
  );

  // audio preview loop within [startMs, startMs + windowMs]
  const audioRef = React.useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setPlaying] = React.useState(false);
  const [posMs, setPosMs] = React.useState(0);
  const pendingHeadRef = React.useRef<number | null>(null);

const [localAudioUrl, setLocalAudioUrl] = React.useState<string | null>(null);

React.useEffect(() => {
  if (track?.file instanceof File) {
    const url = URL.createObjectURL(track.file);
    setLocalAudioUrl(url);
    return () => URL.revokeObjectURL(url);
  }
}, [track?.file]);

const audioSrc =
  localAudioUrl ||
  toAbsoluteHttp(track?.audioUrl) ||
  toAbsoluteHttp(track?.url) ||
  toAbsoluteHttp(track?.s3Url) ||
  undefined;

React.useEffect(() => {
  console.log('🎵 track.file:', track?.file);
  console.log('🎵 localAudioUrl:', localAudioUrl);
  console.log('🎵 audioSrc:', audioSrc);
}, [track?.file, localAudioUrl, audioSrc]);


  // keep pendingStart in sync if startMs changes elsewhere
React.useEffect(() => { setPendingStart(startMs); }, [startMs]);

// helper
const clearCommitTimer = () => {
  if (commitTimerRef.current) {
    window.clearTimeout(commitTimerRef.current);
    commitTimerRef.current = null;
  }
};


  React.useEffect(() => {
    const a = audioRef.current;
    if (!a || !audioSrc) return;

    const onTime = () => {
      const cur = a.currentTime * 1000;
      setPosMs(cur);
      const s = startMsRef.current;
      const e = Math.min(s + windowMs, totalMs);
      if (a.paused) return;
      if (cur >= e - 5) {
        // loop
        a.currentTime = s / 1000;
        pendingHeadRef.current = s;
        setPosMs(s);
        a.play().catch(() => {});
      }
    };

    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    a.addEventListener('timeupdate', onTime);
    a.addEventListener('play', onPlay);
    a.addEventListener('pause', onPause);

    return () => {
      a.removeEventListener('timeupdate', onTime);
      a.removeEventListener('play', onPlay);
      a.removeEventListener('pause', onPause);
    };
  }, [audioSrc, windowMs, totalMs]);

  const togglePlay = async () => {
    const a = audioRef.current;
    if (!a) return;
    try {
      if (!isPlaying) {
        const s = Math.max(0, Math.min(startMsRef.current, maxStart));
        startMsRef.current = s;
        _setStartMs(s);
        a.currentTime = s / 1000;
        pendingHeadRef.current = s;
        setPosMs(s);
        await a.play();
      } else {
        a.pause();
      }
    } catch {}
  };

  // Slider
  const clampStart = (s: number) => Math.max(0, Math.min(s, maxStart));
  const snap = (s: number) => clampStart(Math.round(s / SNAP_MS) * SNAP_MS);

  React.useEffect(() => {
    const id = 'chrome-like-range-css';
    if (document.getElementById(id)) return;
  
    const style = document.createElement('style');
    style.id = id;
    style.textContent = `
      /* Base track */
      .rangeChrome {
        -webkit-appearance: none;
        appearance: none;
        width: 100%;
        height: 6px;
        background: #ddd;                  /* fallback; WebKit gets inline gradient */
        border-radius: 999px;
        outline: none;
      }
  
      /* WebKit (Chrome/Safari/Edge Chromium) */
      .rangeChrome::-webkit-slider-runnable-track {
        height: 6px;
        background: transparent;           /* we paint with inline gradient on input */
        border-radius: 999px;
      }
      .rangeChrome::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        width: 18px;
        height: 18px;
        border-radius: 50%;
        background: #1a73e8;               /* blue */
        border: none;                       /* ← no white border */
        margin-top: -6px;                   /* center on 6px track */
        box-shadow: 0 1px 3px rgba(0,0,0,0.28);
        cursor: pointer;
      }
      .rangeChrome:active::-webkit-slider-thumb { transform: scale(0.98); }
  
      /* Firefox */
      .rangeChrome::-moz-range-track {
        height: 6px;
        background: #ddd;
        border-radius: 999px;
      }
      .rangeChrome::-moz-range-progress {
        height: 6px;
        background: #1a73e8;               /* blue filled portion */
        border-radius: 999px 0 0 999px;
      }
      .rangeChrome::-moz-range-thumb {
        width: 18px; height: 18px; border-radius: 50%;
        background: #1a73e8; border: none; /* ← no border */
        box-shadow: 0 1px 3px rgba(0,0,0,0.28);
        cursor: pointer;
      }
  
      /* IE/old Edge */
      .rangeChrome::-ms-track {
        height: 6px; background: transparent;
        border-color: transparent; color: transparent;
      }
      .rangeChrome::-ms-fill-lower { background: #1a73e8; border-radius: 999px; }
      .rangeChrome::-ms-fill-upper { background: #ddd;    border-radius: 999px; }
      .rangeChrome::-ms-thumb {
        width: 18px; height: 18px; border-radius: 50%;
        background: #1a73e8; border: none; /* ← no border */
        box-shadow: 0 1px 3px rgba(0,0,0,0.28);
        cursor: pointer;
        margin-top: 0;
      }
    `;
    document.head.appendChild(style);
  }, []);
  

  // preview + save (server ffmpeg)
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const [previewFilename, setPreviewFilename] = React.useState<string | null>(null);

  const buildPreview = async () => {
    if (!track?._id) { alert(t('gallery.chooseTrack.selectTrack')); return; }
  
    // Must have a File or an HTTP(S) URL
    const httpVideo = isHttp(videoUrl ?? '') ? (videoUrl as string) : undefined;
    if (!srcFileRef.current && !httpVideo) {
      alert(t('gallery.chooseTrack.noVideoSource'));
      return;
    }
  
    setIsProcessing(true);
    try {
      let fileForServer: File;
      if (srcFileRef.current) {
        // ✅ best path
        fileForServer = srcFileRef.current;
      } else {
        // ✅ HTTP(S) fallback
        const resp = await fetch(httpVideo!);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const blob = await resp.blob();
        fileForServer = new File([blob], 'video.mp4', { type: blob.type || 'video/mp4' });
      }
  
      const fd = new FormData();
      fd.append('video', fileForServer);
      fd.append('mixOriginal', 'true');
      fd.append('volume', '1.2');
      fd.append('offset', String(Math.floor(startMsRef.current / 1000)));
      fd.append('duckMode', 'duck-voice');
      fd.append('duckThresholdDb', '-28');
      fd.append('duckRatio', '30');
      fd.append('duckAttackMs', '10');
      fd.append('duckReleaseMs', '250');
      fd.append('duckFocusVoice', 'true');
      fd.append('filterId', filterId);
    
      if (track.file instanceof File) {
  fd.append('audio', track.file);
} else {
  fd.append('trackId', track._id);
}
      
      const { data } = await api.post('/videopreview-ad', fd);
      if (!data?.previewUrl) { alert('Failed to generate preview.'); return; }
  
      const abs = toAbsoluteHttp(data.previewUrl) || `${getApiBaseUrl()}${data.previewUrl}`;
      setPreviewUrl(abs || null);
      if (audioRef.current) audioRef.current.pause();
      setPreviewFilename(data.filename || null);
    } catch (e) {
      console.error('Preview error', e);
      alert(t('gallery.chooseTrack.previewFailed'));
    } finally {
      setIsProcessing(false);
    }
  };
  

  const handleSave = async () => {
    if (!previewUrl || !previewFilename) {
      alert(t('gallery.chooseTrack.generatePreviewFirst'));
      return;
    }
    setIsProcessing(true);
    try {
      const httpUrl = toAbsoluteHttp(previewUrl);
      if (!httpUrl) throw new Error('Preview URL is not HTTP(S).');

      const res = await fetch(httpUrl);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const mixedFile = new File([blob], 'mixed.mp4', { type: 'video/mp4' });

      const id = editTargetId || `preview-${Date.now()}`;
      addOrReplaceMediaItem({
        id,
        file: mixedFile,
        musicTitle: track?.title ?? null,
        musicArtist: track?.artist ?? null,
        filterId,
      });
      // optional duplicate write:
      // if (track) updateMediaMusic(id, track.title, track.artist);

      try {
        await api.delete(`/videopreview-ad/${encodeURIComponent(previewFilename)}`);
      } catch (e) {
        console.warn('Preview cleanup failed (non-fatal):', e);
      }

      navigate('/adupload', {
        state: {
          sidebarData,
          previewFile: mixedFile,
          previewFilename,
          editTargetId: id,
          isPreview: true,
        },
      });
    } catch (e) {
      console.error('Save failed', e);
      alert(t('gallery.chooseTrack.saveFailed'));
    } finally {
      setIsProcessing(false);
    }
  };

  const intervalLabel = React.useMemo(() => fmt(windowMs, 'ms'), [windowMs]);
  const endMs = Math.min(startMs + windowMs, totalMs);
  const playedLocalMs = Math.min(Math.max(posMs - uiStartMs, 0), windowMs);
  const playedWidthPx  = playedLocalMs * ppm;

  const effectiveTrackUrl = audioSrc;


    React.useEffect(() => clearCommitTimer, []);

    React.useEffect(() => {
      console.debug('[AdChooseTrack] using videoUrl:', videoUrl, 'file?', !!srcFileRef.current);
    }, [videoUrl]);

    
  return (
    <div style={styles.page}>
      {/* hidden probes */}
<video
  ref={hiddenVideoRef}
  src={videoUrl ?? undefined}
  style={{ display: 'none' }}
  preload="metadata"
  playsInline
/>

<audio
  src={effectiveTrackUrl}
  controls
  style={{ width: '100%' }}
/>

<audio
  ref={audioRef}
  src={effectiveTrackUrl}
  preload="auto"
  style={{ display: 'none' }}
/>

      {/* Top bar */}
      <div style={styles.topBar}>
  {/* glassy back button with chevron */}
  <button
    onClick={() => navigate(-1)}
    aria-label="t('common.back')"
    title="t('common.back')"
    style={styles.glassBtn}
    onMouseEnter={(e) => {
      const el = e.currentTarget as HTMLButtonElement;
      el.style.background = 'rgba(0,0,0,0.7)';
      el.style.borderColor = 'rgba(255,255,255,0.28)';
    }}
    onMouseLeave={(e) => {
      const el = e.currentTarget as HTMLButtonElement;
      el.style.background = 'rgba(0,0,0,0.55)';
      el.style.borderColor = 'rgba(255,255,255,0.18)';
    }}
    onMouseDown={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(0.96)'; }}
    onMouseUp={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)'; }}
  >
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M15 19L8 12L15 5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  </button>

  <div style={{ flex: 1 }} />

  {/* glassy close button that navigates to /gallery-posting (preserves sidebarData) */}
  <button
    onClick={() =>
      navigate('/adupload', {
        state: {
          reset: false,
          sidebarData, // ← comes from location.state already
        },
      })
    }
    aria-label="t('common.close')"
    title="t('common.close')"
    style={styles.glassBtn}
    onMouseEnter={(e) => {
      const el = e.currentTarget as HTMLButtonElement;
      el.style.background = 'rgba(0,0,0,0.7)';
      el.style.borderColor = 'rgba(255,255,255,0.28)';
    }}
    onMouseLeave={(e) => {
      const el = e.currentTarget as HTMLButtonElement;
      el.style.background = 'rgba(0,0,0,0.55)';
      el.style.borderColor = 'rgba(255,255,255,0.18)';
    }}
    onMouseDown={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(0.96)'; }}
    onMouseUp={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)'; }}
  >
    {/* “X” icon */}
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  </button>
</div>


      <div style={styles.layout}>
        {/* Left (controls) */}
        <aside style={styles.sidebar}>
          

          <div style={{ marginTop: 8, marginBottom: 10 }}>
            <div style={styles.title} title={track?.title || t('gallery.chooseTrack.trimMusicTitle')}>
              {track?.title || t('gallery.chooseTrack.trimMusicTitle')}
            </div>
            {track?.artist && (
  <div style={styles.artist} title={track.artist}>
    {track.artist}
  </div>
)}
          </div>

          {/* Optional track-only audio control */}
          {effectiveTrackUrl && (
            <div style={{ marginTop: 12 }}>
{track?.title && (
  <div style={styles.title} title={track.title}>
    {track.title}
  </div>
)}
            </div>
          )}
        </aside>

        {/* Right (timeline + video/preview) */}
        <main style={styles.main}>
        <div style={styles.headerRow}>
            <span style={styles.intervalBadge}>{intervalLabel}</span>
            
            <Button
  onClick={togglePlay}
  aria-label={isPlaying ? t('gallery.chooseTrack.pause')  : t('gallery.chooseTrack.play')}
  style={{ ...styles.outlineBtn, ...styles.mediaControlBtn }}
>
  <span style={styles.mediaControlIcon}>{isPlaying ? '⏸' : '▶'}</span>
  <span>{isPlaying ? t('gallery.chooseTrack.pause')  : t('gallery.chooseTrack.play')}</span>
</Button>
            <div style={{ flex: 1 }} />
          </div>
          {/* Timeline rail */}
          <div style={styles.timelineWrap} ref={railRef}>
            <div style={{ ...styles.rail, height: TRACK_H }} />
            {/* Wave background */}
            <div style={{ position: 'absolute', inset: 0, transform: `translateX(${snappedWaveX}px)`, zIndex: 1, pointerEvents: 'none' }}>
              <Wave bars={bars} height={TRACK_H} color="rgba(255,255,255,0.28)" />
            </div>

            {/* Played region */}
            <div style={{ position: 'absolute', inset: 0, zIndex: 3, pointerEvents: 'none' }}>
              <div
                style={{
                  position: 'absolute',
                  left: selLeft,
                  width: playedWidthPx,
                  height: TRACK_H,
                  overflow: 'hidden',
                  background: '#282828',
                  borderRadius: 10,
                  boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
                }}
              >
                <div style={{ width: waveWidthPx, transform: `translateX(${snappedWaveX}px)` }}>
                  <Wave bars={bars} height={TRACK_H} color="#ffd76a" />
                </div>
              </div>
            </div>

            {/* Selection frame */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: selLeft,
                width: selW,
                height: TRACK_H-2,
                border: '1px solid #ffd76a',
                borderRadius: 10,
                zIndex: 4,
                pointerEvents: 'none',
              }}
            >
              <div style={{ position: 'absolute', top: -6, left: -Math.floor(HANDLE_W / 2), width: HANDLE_W, height: TRACK_H + 12, borderRadius: 6, background: '#ffd76a' }} />
              <div style={{ position: 'absolute', top: -6, right: -Math.floor(HANDLE_W / 2), width: HANDLE_W, height: TRACK_H + 12, borderRadius: 6, background: '#ffd76a' }} />
            </div>
          </div>

          {/* Times row */}
<div style={styles.timesRow}>
  <span style={styles.timeTxt}>
    {fmt(liveStartMs, 'ms')} → {fmt(liveEndMs, 'ms')}
  </span>
  <span style={styles.timeTxt}>{fmt(totalMs, 'ms')}</span>
</div>

{/* Slider fallback */}
<div style={{ marginTop: 12 }}>
  {(() => {
    const value = Math.min(pendingStart, maxStart);
    const max = Math.max(1, maxStart);
    const progress = value / max;
    const pct = Math.round((value / max) * 100);
    const halfThumbPx = THUMB_W / 2;
    const epsilonPx = 0.5;
    const bgWidth = `calc(${halfThumbPx}px + (100% - ${THUMB_W}px) * ${progress} - ${epsilonPx}px)`;

    return (
      <input
  type="range"
  className="rangeChrome"
  min={0}
  max={max}
  step={100}
  value={value}
  style={{
    background: `linear-gradient(#1a73e8, #1a73e8) left / ${bgWidth} 100% no-repeat, #ddd`,
    backgroundPosition: 'left center',
  }}
  onMouseDown={() => setDragging(true)}
  onTouchStart={() => setDragging(true)}

  onMouseUp={() => {
    clearCommitTimer();
    const snapped = snap(pendingStart);
    setStartMs(snapped);
    setPendingStart(snapped);
    setDragging(false);
    if (isPlaying && audioRef.current) {
      audioRef.current.currentTime = snapped / 1000;
    }
  }}
  onTouchEnd={() => {
    clearCommitTimer();
    const snapped = snap(pendingStart);
    setStartMs(snapped);
    setPendingStart(snapped);
    setDragging(false);
    if (isPlaying && audioRef.current) {
      audioRef.current.currentTime = snapped / 1000;
    }
  }}

  onChange={(e) => {
    const s = clampStart(Number(e.target.value));
    setPendingStart(s);

    // live audio scrub while dragging
    if (isPlaying && audioRef.current) {
      audioRef.current.currentTime = s / 1000;
    }

    // light throttle in case of huge FPS
    clearCommitTimer();
    commitTimerRef.current = window.setTimeout(() => {
      if (!isDragging) {                    // only auto-commit when not dragging
        const snapped = snap(s);
        setStartMs(snapped);
        setPendingStart(snapped);
        if (isPlaying && audioRef.current) {
          audioRef.current.currentTime = snapped / 1000;
        }
      }
    }, 80);
  }}
/>
    );
  })()}
</div>


<div style={styles.controlsRow}>
  <div style={styles.bigActionStack}>
    <Button
      onClick={buildPreview}
      // 🚫 no disabled here — always enabled as requested
      style={{ ...styles.bigActionBtn }}
    >
      <span style={styles.bigActionIcon}>🎞️</span>
      <span>{t('gallery.chooseTrack.mergeCta')}</span>
    </Button>

    <Button
      onClick={handleSave}
      disabled={!previewUrl || isProcessing}  // ✅ only Save is disabled like before
      style={{ ...styles.bigActionBtn, ...styles.bigActionPrimary, ...( !previewUrl || isProcessing ? styles.bigActionDisabled : {} ) }}
    >
      <span style={styles.bigActionIcon}>✅</span>
      <span>{t('gallery.chooseTrack.saveCta')}</span>
    </Button>
  </div>
            <div style={{ flex: 1 }} />
          </div>
          {/* Video / Preview */}
          <div style={styles.playerWrap}>
            {isProcessing && <div style={{ color: '#ffd76a', marginBottom: 8 }}>{t('gallery.chooseTrack.processing')}</div>}
            {previewUrl ? (
  <SmartVideoPlayer
  key={`${previewUrl ? `pv-${previewUrl}` : `src-${videoUrl}`}|${filterId}`}
  videoUrl={(previewUrl || videoUrl)!}
    startUnmuted={true}   // 🔊 start with sound
    autoplay={false}      // 👈 avoid blocked autoplay; user click to play
    controls
    videoStyle={{
      height: '100%',
      width: '100%',
      objectFit: 'contain',
      filter: cssFilter ?? 'none',      // ← apply filter on the <video>
    }}
  />
) : videoUrl ? (
  <SmartVideoPlayer
    key={`src-${videoUrl}|${filterId}`}
    videoUrl={videoUrl}
    startUnmuted={true}
    autoplay={false}
    controls
    videoStyle={{
      height: '100%',
      width: '100%',
      objectFit: 'contain',
      filter: cssFilter ?? 'none',   // ← apply filter here too
    }}
  />
) : (
  <div style={{ color: '#777' }}>{t('gallery.chooseTrack.noVideo')}</div>
)}

          </div>
        </main>
      </div>
    </div>
  );
};

function Wave({ bars, height, color }: { bars: number[]; height: number; color: string }) {
  const contentWidth = bars.length * BAR_STEP;
  return (
    <div style={{ width: contentWidth, display: 'flex', alignItems: 'center', gap: BAR_GAP }}>
      {bars.map((vRaw, i) => {
        const v = vRaw ?? 0.5;
        const h = Math.max(6, Math.round(v * height));
        return (
          <div key={i} style={{
            width: BAR_W,
            height: h,
            borderRadius: 1,
            alignSelf: 'center',
            background: color,
            margin: `0 ${BAR_GAP}px`,
          }} />
        );
      })}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    background: '#0b0b0b',
    color: '#e6e9f3',
  },
  topBar: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 12px',
    background: 'transparent',      
    top: 0,
    zIndex: 20,
  },

  glassBtn: {
  width: 38,
  height: 38,
  borderRadius: 9999,
  display: 'grid',
  placeItems: 'center',
  border: '1px solid rgba(255,255,255,0.18)',
  background: 'rgba(0,0,0,0.55)',
  color: '#ccc',
  cursor: 'pointer',
  backdropFilter: 'blur(6px)',
  transition: 'transform 120ms ease, background 120ms ease, border-color 120ms ease',
},

  timerBadge: {
    padding: '6px 8px', borderRadius: 8,
    background: 'rgba(0,0,0,0.35)', fontVariant: 'tabular-nums',
    fontSize: 12,
  },
  layout: {
    display: 'grid',
    gridTemplateColumns: '360px 1fr',
    gap: 16,
    padding: 16,
    marginTop: -18,
  },
  sidebar: {
    background: '#0f1115',
    border: '1px solid #1f2330',
    borderRadius: 16,
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
  },
  headerRow: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 },
  intervalBadge: { color: '#000', background: '#ffd76a', fontWeight: 800, fontSize: 12, padding: '4px 8px', borderRadius: 8, },
  
  title: { fontSize: 16, fontWeight: 800, color: '#fff' },
  artist: { marginTop: 2, fontSize: 12, color: '#9aa0a6' },
  metaRow: { display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#9aa0a6', marginTop: 4 },
  label: { display: 'block', fontWeight: 600, marginBottom: 6 },
  helpTxt: { fontSize: 12, color: '#9aa0a6', marginTop: 4 },
  controlsRow: { display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 35, zIndex: 2, },
  outlineBtn: { background: 'transparent', border: '1px solid #ffd76a', color: '#ffd76a' },
  mediaControlBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 10,
    padding: '6px 12px',     // bigger hit area
    fontSize: 12,             // larger text
    lineHeight: 1.1,
    borderRadius: 10,
    cursor: 'pointer',
    userSelect: 'none',
  },
  mediaControlIcon: {
    fontSize: 14,             // icon size
    lineHeight: 1,
    transform: 'translateY(-1px)',
  },

bigActionStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    minWidth: 220,            // gives a nice stable width
  },

  bigActionBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 12,
    padding: '6px 12px',     // bigger hit area
    fontSize: 13,             // larger text
    lineHeight: 1.1,
    borderRadius: 12,
    border: '1px solid #ffd76a',
    background: 'transparent',
    color: '#ffd76a',
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'transform 120ms ease, opacity 120ms ease',
  },

  bigActionPrimary: {
    // optional stronger look for “Save”
    background: '#ffd76a',
    color: '#1a1a1a',
    borderColor: '#ffd76a',
  },

  bigActionDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
    pointerEvents: 'none',
    filter: 'grayscale(30%)',
  },

  bigActionIcon: {
    fontSize: 13,
    lineHeight: 1,
    transform: 'translateY(-1px)',
  },
  
  main: {
    background: '#0f1115',
    border: '1px solid #1f2330',
    borderRadius: 16,
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
  },
  timelineWrap: {
    position: 'relative',
    height: TRACK_H,
    borderRadius: 10,
    overflow: 'hidden',
    background: 'transparent',
    marginTop: 10,
  },
  rail: {
    position: 'absolute', inset: 0,
    borderRadius: 10, background: 'rgba(255,255,255,0.12)',
  },
  timesRow: {
    display: 'flex', justifyContent: 'space-between',
    marginTop: 8, fontSize: 12, color: '#9aa0a6', fontVariant: 'tabular-nums',
  },
  timeTxt: {},
  playerWrap: {
    marginTop: -64,
    display: 'grid', placeItems: 'center',
    minHeight: 260,
    zIndex: 1,
  },
};

export default AdChooseTrack;
